import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Activity, Database, RefreshCcw, ShieldCheck, Clock, CreditCard, AlertTriangle, CheckCircle2, XCircle, BarChart3, Eye, PlayCircle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const ACTION_LABELS = {
  league_deleted: "Lega eliminata",
  user_deleted: "Utente eliminato",
  user_password_reset: "Password reimpostata",
  user_ban_toggled: "Ban toggled",
  impersonate: "Login as",
  broadcast_sent: "Broadcast inviato",
  superadmin_plan_change: "Piano lega modificato",
};

export default function SuperAdminHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stripe, setStripe] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/health");
      setData(data);
    } finally { setLoading(false); }
  }
  async function loadStripe() {
    setStripeLoading(true);
    setStripeError(null);
    try {
      const { data } = await api.get("/stripe/webhook/health");
      setStripe(data);
    } catch (e) {
      setStripeError(e?.response?.data?.detail || e.message || "Errore");
    } finally { setStripeLoading(false); }
  }
  async function loadAnalytics() {
    setAnalyticsLoading(true);
    try {
      const { data } = await api.get("/superadmin/analytics/summary");
      setAnalytics(data);
    } finally { setAnalyticsLoading(false); }
  }
  useEffect(() => { load(); loadStripe(); loadAnalytics(); }, []);

  if (!data) return <div className="p-8 text-slate-400">Caricamento…</div>;

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black tracking-tight" data-testid="sa-health-title">Salute Sistema</h1>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-700 hover:border-rose-500 hover:bg-rose-500/10 text-sm text-slate-300"
        >
          <RefreshCcw size={14}/> Ricarica
        </button>
      </div>

      <div className="grid sm:grid-cols-4 gap-4">
        <div className={`rounded-xl border p-4 ${data.db_ok ? "border-emerald-500/30 bg-emerald-500/10" : "border-rose-500/30 bg-rose-500/10"}`}>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-300 flex items-center gap-2">
            <Database size={14}/> MongoDB
          </div>
          <div className="text-2xl font-black mt-2 font-mono">
            {data.db_ok ? "OK" : "OFF"}
          </div>
        </div>
        <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-300">Storage</div>
          <div className="text-2xl font-black mt-2 font-mono">{data.db_storage_mb} MB</div>
          <div className="text-xs text-slate-400">Dati: {data.db_data_mb} MB</div>
        </div>
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-300">Collections</div>
          <div className="text-2xl font-black mt-2 font-mono">{data.db_collections}</div>
        </div>
        <div className="rounded-xl border border-violet-500/30 bg-violet-500/10 p-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-300">Oggetti</div>
          <div className="text-2xl font-black mt-2 font-mono">{data.db_objects.toLocaleString()}</div>
        </div>
      </div>

      {/* Analytics Visitors — Iter 71.8 */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden" data-testid="sa-analytics-card">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2 text-sm font-bold">
          <BarChart3 size={16} className="text-cyan-400"/> Analytics · Visite Sito & Demo
          <button
            onClick={loadAnalytics}
            disabled={analyticsLoading}
            data-testid="sa-analytics-reload-btn"
            className="ml-auto inline-flex items-center gap-2 px-2.5 py-1 rounded-md border border-slate-700 hover:border-cyan-500 hover:bg-cyan-500/10 text-xs text-slate-300"
          >
            <RefreshCcw size={12}/> Ricarica
          </button>
        </div>
        {!analytics ? (
          <div className="p-6 text-slate-400 text-sm">Carico analytics…</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-4 gap-3 p-4 border-b border-slate-800">
              <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3" data-testid="sa-analytics-visits-total">
                <div className="text-[9px] uppercase tracking-widest font-bold text-slate-400 flex items-center gap-1"><Eye size={10}/> Visite totali</div>
                <div className="text-2xl font-black mt-1 font-mono text-cyan-300">{analytics.totals.visits_all_time}</div>
                <div className="mt-1 text-[10px] text-slate-500">tutti i tempi</div>
              </div>
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                <div className="text-[9px] uppercase tracking-widest font-bold text-slate-400">Ultime 24h</div>
                <div className="text-2xl font-black mt-1 font-mono text-emerald-300">{analytics.totals.visits_last_24h}</div>
                <div className="mt-1 text-[10px] text-slate-500">{analytics.totals.visits_last_7d} in 7gg</div>
              </div>
              <div className="rounded-lg border border-purple-500/30 bg-purple-500/5 p-3">
                <div className="text-[9px] uppercase tracking-widest font-bold text-slate-400">Visitatori Unici 7gg</div>
                <div className="text-2xl font-black mt-1 font-mono text-purple-300">{analytics.totals.unique_visitors_7d}</div>
                <div className="mt-1 text-[10px] text-slate-500">{analytics.totals.unique_visitors_30d} in 30gg</div>
              </div>
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="sa-analytics-demo-total">
                <div className="text-[9px] uppercase tracking-widest font-bold text-slate-400 flex items-center gap-1"><PlayCircle size={10}/> Demo aperte</div>
                <div className="text-2xl font-black mt-1 font-mono text-amber-300">{analytics.totals.demo_opens_all_time}</div>
                <div className="mt-1 text-[10px] text-slate-500">{analytics.totals.demo_opens_last_7d} in 7gg</div>
              </div>
            </div>
            {analytics.trend_30d?.length > 0 && (
              <div className="p-4 border-b border-slate-800" data-testid="sa-analytics-trend-chart">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
                  📈 Trend ultimi 30 giorni
                </div>
                <div style={{ width: "100%", height: 200 }}>
                  <ResponsiveContainer>
                    <LineChart data={analytics.trend_30d} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid stroke="#1e293b" strokeDasharray="3 3"/>
                      <XAxis dataKey="day_label" stroke="#64748b" fontSize={10}/>
                      <YAxis stroke="#64748b" fontSize={10} allowDecimals={false}/>
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }}/>
                      <Legend wrapperStyle={{ fontSize: 10, paddingTop: 4 }}/>
                      <Line type="monotone" dataKey="visits" stroke="#22d3ee" strokeWidth={2} dot={{ r: 2 }} name="Visite"/>
                      <Line type="monotone" dataKey="demo_opens" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} name="Demo aperte"/>
                      <Line type="monotone" dataKey="signups" stroke="#a855f7" strokeWidth={2} dot={{ r: 2 }} name="Signup"/>
                      <Line type="monotone" dataKey="checkouts" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} name="Checkout"/>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            {analytics.top_referrers_7d?.length > 0 && (
              <div className="px-4 py-3 border-b border-slate-800">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Top Referrer (7gg)</div>
                <div className="flex flex-wrap gap-2">
                  {analytics.top_referrers_7d.map((r, i) => (
                    <div key={i} className="text-[11px] font-mono px-2 py-1 rounded-md bg-slate-800/60 border border-slate-700 text-slate-300 truncate max-w-xs">
                      {r.referrer.replace(/^https?:\/\//, "").slice(0, 45)} <span className="text-cyan-400 font-bold ml-1">×{r.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="overflow-x-auto max-h-56 overflow-y-auto">
              <table className="w-full text-xs" data-testid="sa-analytics-recent-table">
                <thead className="sticky top-0 bg-slate-900 border-b border-slate-800">
                  <tr className="text-left text-slate-400 uppercase tracking-wider">
                    <th className="px-4 py-2 font-bold">Evento</th>
                    <th className="px-4 py-2 font-bold">Path</th>
                    <th className="px-4 py-2 font-bold">Referrer</th>
                    <th className="px-4 py-2 font-bold">IP hash</th>
                    <th className="px-4 py-2 font-bold">Quando</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {analytics.recent_events.length === 0 ? (
                    <tr><td colSpan={5} className="p-6 text-center text-slate-500">Nessun evento ancora tracciato</td></tr>
                  ) : analytics.recent_events.map((e, i) => (
                    <tr key={i} className="hover:bg-slate-800/40">
                      <td className="px-4 py-1.5">
                        <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                          e.event === "visit" ? "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" :
                          e.event === "demo_open" ? "bg-amber-500/15 text-amber-300 border-amber-500/30" :
                          "bg-slate-700/40 text-slate-400 border-slate-600"
                        }`}>{e.event}</span>
                      </td>
                      <td className="px-4 py-1.5 font-mono text-slate-400 truncate max-w-[100px]">{e.path || "—"}</td>
                      <td className="px-4 py-1.5 font-mono text-slate-500 truncate max-w-[180px]">{(e.referrer || "—").replace(/^https?:\/\//, "")}</td>
                      <td className="px-4 py-1.5 font-mono text-slate-600 text-[10px]">{e.ip_hash?.slice(0, 8)}</td>
                      <td className="px-4 py-1.5 text-[10px] text-slate-500 font-mono">{(e.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Stripe Webhook Health — SuperAdmin only */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden" data-testid="sa-stripe-webhook-card">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2 text-sm font-bold">
          <CreditCard size={16} className="text-violet-400"/> Diagnostica Stripe Webhook
          {stripe && (
            stripe.healthy
              ? <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 font-bold" data-testid="sa-stripe-status-badge"><CheckCircle2 size={10}/> Healthy</span>
              : <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300 font-bold" data-testid="sa-stripe-status-badge"><AlertTriangle size={10}/> Attenzione</span>
          )}
          <button
            onClick={loadStripe}
            disabled={stripeLoading}
            data-testid="sa-stripe-reload-btn"
            className="ml-auto inline-flex items-center gap-2 px-2.5 py-1 rounded-md border border-slate-700 hover:border-violet-500 hover:bg-violet-500/10 text-xs text-slate-300"
          >
            <RefreshCcw size={12}/> Ricarica
          </button>
        </div>

        {stripeError && (
          <div className="p-4 text-sm text-rose-300 bg-rose-500/10 border-b border-rose-500/30 flex items-center gap-2" data-testid="sa-stripe-error">
            <XCircle size={14}/> {stripeError}
          </div>
        )}

        {!stripe && !stripeError && (
          <div className="p-6 text-slate-400 text-sm">Caricamento diagnostica…</div>
        )}

        {stripe && (
          <>
            <div className="grid sm:grid-cols-4 gap-3 p-4 border-b border-slate-800">
              <div className={`rounded-lg border p-3 ${stripe.configured ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/30 bg-rose-500/10"}`} data-testid="sa-stripe-secret-tile">
                <div className="text-[9px] uppercase tracking-widest font-bold text-slate-400">Signing Secret</div>
                <div className="text-lg font-black mt-1 font-mono flex items-center gap-2">
                  {stripe.configured
                    ? <><CheckCircle2 size={16} className="text-emerald-400"/> Configurato</>
                    : <><XCircle size={16} className="text-rose-400"/> Mancante</>}
                </div>
                {stripe.secret_prefix && (
                  <div className="mt-1 text-[10px] text-slate-500 font-mono truncate" data-testid="sa-stripe-secret-prefix">
                    {stripe.secret_prefix} ({stripe.secret_length} ch.)
                  </div>
                )}
              </div>
              <div className={`rounded-lg border p-3 ${stripe.stripe_mode === "live" ? "border-rose-500/30 bg-rose-500/5" : "border-sky-500/30 bg-sky-500/5"}`} data-testid="sa-stripe-mode-tile">
                <div className="text-[9px] uppercase tracking-widest font-bold text-slate-400">Modalità</div>
                <div className="text-lg font-black mt-1 font-mono uppercase">{stripe.stripe_mode}</div>
                <div className="mt-1 text-[10px] text-slate-500 font-mono truncate">
                  API {stripe.stripe_api_key_prefix || "—"}
                </div>
              </div>
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="sa-stripe-paid-tile">
                <div className="text-[9px] uppercase tracking-widest font-bold text-slate-400">Pagati (30gg)</div>
                <div className="text-2xl font-black mt-1 font-mono text-emerald-300">{stripe.counters.paid_last_30d}</div>
                <div className="mt-1 text-[10px] text-slate-500">Totale storico: {stripe.counters.paid_total}</div>
              </div>
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="sa-stripe-pending-tile">
                <div className="text-[9px] uppercase tracking-widest font-bold text-slate-400">Pending / Falliti</div>
                <div className="text-2xl font-black mt-1 font-mono">
                  <span className="text-amber-300">{stripe.counters.pending}</span>
                  <span className="text-slate-500 mx-1">/</span>
                  <span className="text-rose-300">{stripe.counters.failed_or_expired}</span>
                </div>
              </div>
            </div>

            {/* Istruzioni operative sul mismatch secret */}
            {!stripe.healthy && (
              <div className="p-4 border-b border-slate-800 bg-amber-500/5 text-sm text-amber-100/90 space-y-2">
                <div className="font-bold flex items-center gap-2"><AlertTriangle size={14}/> Come confrontare il secret con Stripe</div>
                <ol className="list-decimal ml-5 space-y-1 text-xs text-amber-100/80">
                  <li>Apri <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer" className="underline hover:text-white">Stripe Dashboard → Developers → Webhooks</a></li>
                  <li>Seleziona l&apos;endpoint verso <code className="bg-black/40 px-1 py-0.5 rounded">/api/stripe/webhook</code></li>
                  <li>Clicca su <b>&quot;Reveal signing secret&quot;</b> e confronta i primi 10 caratteri con <code className="bg-black/40 px-1 py-0.5 rounded">{stripe.secret_prefix || "(non configurato)"}</code></li>
                  <li>Se non combaciano: aggiorna <code className="bg-black/40 px-1 py-0.5 rounded">STRIPE_WEBHOOK_SECRET</code> nel <code>.env</code> di produzione e ridispiega</li>
                </ol>
              </div>
            )}

            {/* Recent payments */}
            <div className="overflow-x-auto max-h-72 overflow-y-auto">
              <table className="w-full text-xs" data-testid="sa-stripe-recent-table">
                <thead className="sticky top-0 bg-slate-900 border-b border-slate-800">
                  <tr className="text-left text-slate-400 uppercase tracking-wider">
                    <th className="px-4 py-2 font-bold">Session</th>
                    <th className="px-4 py-2 font-bold">Tipo</th>
                    <th className="px-4 py-2 font-bold">Lega / Email</th>
                    <th className="px-4 py-2 font-bold text-right">Importo</th>
                    <th className="px-4 py-2 font-bold">Status</th>
                    <th className="px-4 py-2 font-bold">Aggiornato</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {stripe.recent_payments.length === 0 ? (
                    <tr><td colSpan={6} className="p-6 text-center text-slate-500">Nessun pagamento registrato</td></tr>
                  ) : stripe.recent_payments.map((p) => (
                    <tr key={p.session_id} className="hover:bg-slate-800/40">
                      <td className="px-4 py-2 font-mono text-[10px] text-slate-400">{(p.session_id || "").slice(0, 24)}…</td>
                      <td className="px-4 py-2 text-slate-300">{p.type || "league_activation"}</td>
                      <td className="px-4 py-2 text-slate-300 truncate max-w-[220px]">
                        <div className="truncate">{p.league_name || p.league_id || "—"}</div>
                        <div className="text-[10px] text-slate-500 truncate">{p.user_email}</div>
                      </td>
                      <td className="px-4 py-2 font-mono text-right text-slate-200">
                        {p.amount != null ? `${p.amount.toFixed(2)} ${(p.currency || "eur").toUpperCase()}` : "—"}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded border ${
                          p.payment_status === "paid"    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" :
                          p.payment_status === "pending" ? "bg-amber-500/15 border-amber-500/40 text-amber-300" :
                                                            "bg-rose-500/15 border-rose-500/40 text-rose-300"
                        }`}>{p.payment_status || "—"}</span>
                      </td>
                      <td className="px-4 py-2 text-[10px] text-slate-500 font-mono">
                        {(p.updated_at || p.created_at || "").slice(0, 16).replace("T", " ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2 text-sm font-bold">
            <ShieldCheck size={16} className="text-rose-400"/> Audit Super Admin
            <div className="ml-auto text-xs text-slate-500 font-mono">{data.superadmin_audit.length} eventi</div>
          </div>
          <ul className="divide-y divide-slate-800 max-h-96 overflow-y-auto text-sm">
            {data.superadmin_audit.length === 0 ? (
              <li className="p-4 text-slate-500 text-center">Nessun evento registrato</li>
            ) : data.superadmin_audit.map((a, idx) => (
              <li key={a.audit_id || `audit-${idx}`} className="p-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded bg-rose-500/15 border border-rose-500/30 text-rose-300">
                    {ACTION_LABELS[a.action] || a.action}
                  </span>
                  <span className="text-slate-400 text-xs">{a.actor_email}</span>
                </div>
                {a.meta && (
                  <pre className="mt-1 text-[10px] text-slate-500 font-mono overflow-x-auto">
                    {JSON.stringify(a.meta, null, 0)}
                  </pre>
                )}
                <div className="mt-1 text-[10px] text-slate-600 font-mono flex items-center gap-1">
                  <Clock size={10}/> {(a.created_at || "").slice(0, 16).replace("T", " ")}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2 text-sm font-bold">
            <Activity size={16} className="text-emerald-400"/> Ultime attività (op_logs)
            <div className="ml-auto text-xs text-slate-500 font-mono">{data.recent_op_logs.length} eventi</div>
          </div>
          <ul className="divide-y divide-slate-800 max-h-96 overflow-y-auto text-sm">
            {data.recent_op_logs.length === 0 ? (
              <li className="p-4 text-slate-500 text-center">Nessun evento</li>
            ) : data.recent_op_logs.map((log, idx) => (
              <li key={log.log_id || `oplog-${idx}`} className="p-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded bg-slate-800 text-slate-300">
                    {log.action}
                  </span>
                  <span className="text-xs text-slate-500 font-mono">{log.league_id}</span>
                </div>
                <div className="mt-1 text-[10px] text-slate-600 font-mono flex items-center gap-1">
                  <Clock size={10}/> {(log.created_at || "").slice(0, 16).replace("T", " ")}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
